import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/types/api.generated";
export type { FocusArea } from "@/types/entities";
import { toast } from "sonner";

export interface FocusAreaFilters {
  strategic_plan_id?: string;
  cursor?: string;
  limit?: number;
}

export function useFocusAreas(filters: FocusAreaFilters = {}) {
  return useQuery({
    queryKey: ["focus-areas", filters],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/focus-areas", {
        params: { query: filters },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
  });
}

export function useCreateFocusArea() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateFocusAreaRequest"]) => {
      const { data, error } = await apiClient.POST("/v1/focus-areas", { body });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["focus-areas"] });
      void queryClient.invalidateQueries({ queryKey: ["college-goals"] });
      toast.success("Focus area created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateFocusArea(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["UpdateFocusAreaRequest"]) => {
      const { data, error } = await apiClient.PUT("/v1/focus-areas/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["focus-areas"] });
      void queryClient.invalidateQueries({ queryKey: ["college-goals"] });
      toast.success("Focus area updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteFocusArea() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/focus-areas/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["focus-areas"] });
      void queryClient.invalidateQueries({ queryKey: ["college-goals"] });
      toast.success("Focus area deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
